<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onUnmounted, reactive, ref, watch } from 'vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  catalogPriceToInput,
  catalogProductImage,
} from '../utils/whatsappCatalog';

const props = defineProps({
  product: { type: Object, default: null },
  isEdit: { type: Boolean, default: false },
  isSaving: { type: Boolean, default: false },
  canSubmit: { type: Boolean, default: true },
});

const emit = defineEmits(['submit', 'cancel']);
const form = reactive({
  retailer_id: '',
  name: '',
  description: '',
  price_amount: '',
  sale_price_amount: '',
  currency: 'BRL',
  url: '',
  image_base64: '',
  image_mimetype: '',
  is_hidden: false,
});
const imageName = ref('');
const imagePreview = ref('');
const error = ref('');
let objectUrl = '';

const title = computed(() =>
  props.isEdit ? 'Editar produto' : 'Novo produto'
);
const imageRequired = computed(() => !props.isEdit);

const clearObjectUrl = () => {
  if (objectUrl) URL.revokeObjectURL(objectUrl);
  objectUrl = '';
};

const hydrate = product => {
  clearObjectUrl();
  Object.assign(form, {
    retailer_id: product?.retailer_id || '',
    name: product?.name || '',
    description: product?.description || '',
    price_amount: catalogPriceToInput(product?.price_amount_1000),
    sale_price_amount: catalogPriceToInput(product?.sale_price_amount_1000),
    currency: product?.currency || 'BRL',
    url: product?.url || '',
    image_base64: '',
    image_mimetype: '',
    is_hidden: product?.is_hidden === true,
  });
  imageName.value = '';
  imagePreview.value = catalogProductImage(product);
  error.value = '';
};

watch(() => props.product, hydrate, { immediate: true });
onUnmounted(clearObjectUrl);

const readFile = file =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.toString().split(',').pop());
    reader.onerror = () => reject(new Error('Não foi possível ler a imagem.'));
    reader.readAsDataURL(file);
  });

const onFileChange = async event => {
  const [file] = event.target.files || [];
  if (!file) return;
  if (!['image/jpeg', 'image/png'].includes(file.type)) {
    error.value = 'Envie uma imagem JPEG ou PNG.';
    event.target.value = '';
    return;
  }
  if (file.size > 8 * 1024 * 1024) {
    error.value = 'A imagem deve ter no máximo 8 MB.';
    event.target.value = '';
    return;
  }

  try {
    form.image_base64 = await readFile(file);
    form.image_mimetype = file.type;
    imageName.value = file.name;
    clearObjectUrl();
    objectUrl = URL.createObjectURL(file);
    imagePreview.value = objectUrl;
    error.value = '';
  } catch (requestError) {
    error.value = requestError.message;
  }
};

const submit = () => {
  const name = form.name.trim();
  const currency = form.currency.trim().toUpperCase();
  const price = form.price_amount.toString().trim();
  const imageUrl = catalogProductImage(props.product);

  if (!name || !price || !currency) {
    error.value = 'Preencha nome, preço e moeda.';
    return;
  }
  if (!/^[A-Z]{3}$/.test(currency)) {
    error.value = 'A moeda deve usar três letras, como BRL.';
    return;
  }
  const numericPrice = Number(price);
  const numericSalePrice = form.sale_price_amount
    ? Number(form.sale_price_amount)
    : null;
  if (
    !Number.isFinite(numericPrice) ||
    numericPrice < 0 ||
    (numericSalePrice !== null &&
      (!Number.isFinite(numericSalePrice) || numericSalePrice < 0))
  ) {
    error.value = 'Informe preços válidos e não negativos.';
    return;
  }
  if (imageRequired.value && !form.image_base64) {
    error.value = 'Envie a imagem principal do produto.';
    return;
  }
  if (props.isEdit && !form.image_base64 && !imageUrl) {
    error.value = 'O produto precisa manter uma imagem principal.';
    return;
  }

  error.value = '';
  emit('submit', {
    retailer_id: form.retailer_id.trim(),
    name,
    description: form.description.trim(),
    price_amount: price,
    sale_price_amount: form.sale_price_amount.toString().trim(),
    currency,
    url: form.url.trim(),
    image_url: imageUrl,
    image_base64: form.image_base64,
    image_mimetype: form.image_mimetype,
    is_hidden: form.is_hidden,
  });
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="grid gap-5 lg:grid-cols-[minmax(220px,300px)_minmax(0,1fr)]">
    <section class="rounded-lais border border-lais-border-soft bg-white p-4">
      <div
        class="aspect-square overflow-hidden rounded-lais border border-dashed border-lais-border bg-lais-bg-card"
      >
        <img
          v-if="imagePreview"
          :src="imagePreview"
          :alt="form.name || 'Imagem do produto'"
          class="h-full w-full object-cover"
        />
        <div
          v-else
          class="grid h-full place-items-center p-6 text-center text-lais-text-muted"
        >
          <div>
            <span
              class="i-lucide-image-plus mx-auto mb-3 block size-10 text-lais-primary"
            />
            <p class="text-[13px] font-semibold text-lais-text">
              Adicione uma imagem
            </p>
            <p class="mt-1 text-[12px] leading-5">
              A imagem aparece no catálogo do WhatsApp.
            </p>
          </div>
        </div>
      </div>
      <label
        class="mt-4 flex cursor-pointer items-center justify-center gap-2 rounded-lais-sm border border-lais-border px-3 py-2.5 text-[12px] font-semibold text-lais-primary transition hover:border-lais-primary/50"
      >
        <span class="i-lucide-upload size-4" />
        {{ imageName || (isEdit ? 'Trocar imagem' : 'Escolher imagem') }}
        <input
          type="file"
          accept="image/jpeg,image/png"
          class="sr-only"
          @change="onFileChange"
        />
      </label>
      <p class="mt-2 text-[11px] leading-5 text-lais-text-muted">
        JPEG ou PNG, até 8 MB.
      </p>
    </section>

    <div class="grid gap-4">
      <section class="rounded-lais border border-lais-border-soft bg-white p-5">
        <div class="mb-4">
          <p class="text-[15px] font-semibold text-lais-text">
            Informações do produto
          </p>
          <p class="mt-1 text-[12px] text-lais-text-muted">
            Esses dados são sincronizados com o catálogo da conta conectada.
          </p>
        </div>
        <div class="grid gap-4 md:grid-cols-2">
          <LaisField label="Nome do produto" required class="md:col-span-2">
            <input
              v-model="form.name"
              class="h-11 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
              placeholder="Ex.: Apartamento com varanda"
            />
          </LaisField>
          <LaisField label="Descrição" class="md:col-span-2">
            <textarea
              v-model="form.description"
              rows="4"
              class="w-full rounded-lais-sm border border-lais-border bg-white px-3 py-3 text-[13px] text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
              placeholder="Descreva o produto para seus clientes."
            />
          </LaisField>
          <LaisField label="Código / SKU">
            <input
              v-model="form.retailer_id"
              class="h-11 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
              placeholder="SKU-001"
            />
          </LaisField>
          <LaisField label="Link do produto">
            <input
              v-model="form.url"
              type="url"
              class="h-11 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
              placeholder="https://..."
            />
          </LaisField>
        </div>
      </section>

      <section class="rounded-lais border border-lais-border-soft bg-white p-5">
        <div class="mb-4">
          <p class="text-[15px] font-semibold text-lais-text">
            Preço e visibilidade
          </p>
          <p class="mt-1 text-[12px] text-lais-text-muted">
            O WhatsApp trabalha com o preço em milésimos da unidade monetária.
          </p>
        </div>
        <div class="grid gap-4 md:grid-cols-3">
          <LaisField label="Preço" required>
            <input
              v-model="form.price_amount"
              type="number"
              min="0"
              step="0.01"
              class="h-11 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
              placeholder="1999,90"
            />
          </LaisField>
          <LaisField label="Preço promocional">
            <input
              v-model="form.sale_price_amount"
              type="number"
              min="0"
              step="0.01"
              class="h-11 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
              placeholder="Opcional"
            />
          </LaisField>
          <LaisField label="Moeda" required>
            <input
              v-model="form.currency"
              maxlength="3"
              class="h-11 w-full rounded-lais-sm border border-lais-border bg-white px-3 text-[13px] uppercase text-lais-text outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary/10"
              placeholder="BRL"
            />
          </LaisField>
        </div>
        <label
          class="mt-5 flex cursor-pointer items-start gap-3 rounded-lais-sm border border-lais-border-soft bg-lais-bg-card p-3"
        >
          <input
            v-model="form.is_hidden"
            type="checkbox"
            class="mt-0.5 size-4 rounded border-lais-border text-lais-primary focus:ring-lais-primary"
          />
          <span>
            <span class="block text-[13px] font-semibold text-lais-text">
              Ocultar produto no catálogo
            </span>
            <span class="mt-1 block text-[12px] text-lais-text-muted">
              Mantenha o produto salvo sem exibi-lo para os clientes.
            </span>
          </span>
        </label>
      </section>

      <p
        v-if="error"
        class="rounded-lais-sm border border-red-200 bg-red-50 px-4 py-3 text-[12px] text-red-700"
      >
        {{ error }}
      </p>
      <div class="flex flex-col-reverse justify-end gap-2 sm:flex-row">
        <button
          type="button"
          class="h-11 rounded-lais-sm border border-lais-border px-4 text-[13px] font-semibold text-lais-text-body hover:border-lais-primary/50"
          @click="emit('cancel')"
        >
          Cancelar
        </button>
        <LaisPrimaryButton
          :is-loading="isSaving"
          :disabled="isSaving || !canSubmit"
          icon="i-lucide-check"
          @click="submit"
        >
          {{ title }}
        </LaisPrimaryButton>
      </div>
    </div>
  </div>
</template>
